import {
  arrayOf, elementType, func, string
} from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import useValidation from '../useValidation';
import shallowEqual from '../utils/shallowEqual';
import * as listFormData from '../utils/listFormData';
import connectSectionToContext from './connectSectionToContext';

const ListFormSection = (props) => {
  const propsRef = useRef();
  /* This is some black magic I discovered to fix some serious performance issues.
   *
   * This `propsRef` value is equivalent to `this.props` in a class component. If I reference
   * `props` directly in a `useCallback` function, it will get the version of props that was present
   * when the callback was created by closure. A ref on the other hand, using the `current`
   * property, has its value derived at runtime, meaning you don't have to closure scope the props.
   * This allows `addEntry` and `removeEntry` to have no deps, meaning they will never change.
   * `addEntry` and `removeEntry` were causing all entries passed to the children of
   * `ListFormSection` to change on each keypress, meaning each child had to rerender.
   *
   * In order for these performance enhancements to work, the component rendered for each entry
   * must be memoized. Otherwise when the ListFormSection render prop is called it will create a
   * new tree and render all children. See the nested list form section example for a template.
   */
  propsRef.current = props;
  const {
    // these variables are not used directly but kept here to keep them out of `rest`
    children, component: Component, data: _data,
    errors: _errors, initialErrors, name: _name, onChange: _onChange,
    registerSelfValidatingChild, unregisterSelfValidatingChild, ...rest
  } = props;
  const [nameToIndexMap, setIndexMap] = useState(
    () => listFormData.createNameToIndexMap(props.name, props.data)
  );

  useEffect(() => {
    setIndexMap((prev) => listFormData.patchNameToIndexMap(props.name, prev, props.data));
  }, [props.data, props.name]);

  const onChange = useCallback((changedName, value) => {
    const { data, name, onChange: parentOnChange } = propsRef.current;
    const nextData = listFormData.mapPrivateDataToPublic(nameToIndexMap, value);

    if (shallowEqual(nextData, data)) {
      return;
    }

    parentOnChange(
      name,
      nextData
    );
  }, [nameToIndexMap]);

  const addEntry = useCallback((initialValue) => {
    const { data, name, onChange: parentOnChange } = propsRef.current;
    parentOnChange(
      name,
      [...data, initialValue]
    );
  }, []);

  const removeEntry = useCallback((nameToRemove) => {
    setIndexMap((prev) => {
      const { data, name, onChange: parentOnChange } = propsRef.current;
      const indexForName = prev[nameToRemove];
      const nextMap = listFormData.fromEntries(Object.entries(prev)
        .filter(([current]) => current !== nameToRemove));

      parentOnChange(
        name,
        data.filter((value, index) => index !== indexForName)
      );

      return nextMap;
    });
  }, []);

  const [ChildProvider, childContext, { validate }] = useValidation({
    data: useMemo(
      () => listFormData.mapPublicDataToPrivate(nameToIndexMap, props.data),
      [nameToIndexMap, props.data]
    ),
    initialErrors: useMemo(() => listFormData.mapPublicDataToPrivate(
      nameToIndexMap,
      props.errors || initialErrors
    ), [nameToIndexMap, props.errors, initialErrors]),
    name: props.name,
    onChange
  });

  useEffect(() => {
    registerSelfValidatingChild(props.name, () => (
      validate().then(({ data: nextData, errors: nextErrors, isValid }) => ({
        data: listFormData.mapPrivateDataToPublic(nameToIndexMap, nextData),
        errors: listFormData.mapPrivateDataToPublic(nameToIndexMap, nextErrors),
        isValid
      }))
    ));

    return () => unregisterSelfValidatingChild(props.name);
  }, [
    validate, props.name, nameToIndexMap, registerSelfValidatingChild, unregisterSelfValidatingChild
  ]);

  const sortedEntries = useMemo(
    () => Object.entries(nameToIndexMap).sort(listFormData.sortByIndex),
    [nameToIndexMap]
  );
  const { length } = sortedEntries;
  const childProps = {
    ...rest,
    addEntry,
    entries: useMemo(() => sortedEntries.map(([entryName, index]) => ({
      data: childContext.data[entryName],
      errors: childContext.errors[entryName],
      index,
      key: entryName,
      name: entryName
    })), [childContext.data, childContext.errors, sortedEntries]),
    length,
    nameToIndexMap,
    onChange: childContext.onChange,
    registerValidators: childContext.registerValidators,
    removeEntry,
    unregisterValidators: childContext.unregisterValidators
  };

  return (
    <ChildProvider value={childContext}>
      {Component ? <Component {...childProps} /> : children(childProps)}
    </ChildProvider>
  );
};

ListFormSection.propTypes = {

  /** A render prop to render the list, receives lots of stuff, see storybook
   *  and implementation for details */
  children: func,

  /** */
  component: elementType,

  /** data passed down from the parent context */
  data: arrayOf({}),

  /** errors passed down from the parent context */
  errors: arrayOf({}),

  /** The initial errors for the list */
  initialErrors: arrayOf(arrayOf({})),

  /** The key in the parent data the list is stored in */
  name: string.isRequired,

  /** function passed down from the parent context */
  onChange: func.isRequired,

  /** function passed down from the parent context */
  registerSelfValidatingChild: func.isRequired,

  /** function passed down from the parent context */
  unregisterSelfValidatingChild: func.isRequired
};

ListFormSection.defaultProps = {
  children: undefined,
  component: undefined,
  data: [],
  errors: undefined,
  initialErrors: []
};

export default connectSectionToContext(ListFormSection);
